import { ClientEndpoint, EndpointStatus } from "@app/shared/src/models";
import { deliveryHealth, formatRate, formatWindowSpan, EndpointSeverity } from "@app/shared/src/components/Integrations/endpointHealth";
import { formatAgo, formatDateTime } from "@app/shared/src/components_v2";

/**
 * Whether a truck's relay is actually closing — the one piece of a TMA's health that nothing else on
 * this page can reveal.
 *
 * A unit can be online, on current firmware, with a strong signal and a fresh frame, and still have a
 * horn that has not sounded in a week: the relay is a separate box on the truck's own network, and the
 * device talks to it over HTTP. Every other row in the Health card reports on the device itself and
 * stays green while that happens. The dashboard has shown this all along on the endpoint table
 * (ClientEndpointTable); this is the same verdict, from the same data, phrased for a card row.
 *
 * The verdict itself is deliberately NOT computed here — it comes from the shared deliveryHealth()
 * predicate, so a relay called healthy in the dashboard cannot read degraded here.
 */

/**
 * What the row says. The states that are not a severity — no relay wired, nothing sending to it, no
 * closures recorded — matter as much as the failing one: each is a different phone call, and folding
 * them into a grey "unknown" is how "this truck has no relay configured at all" goes unnoticed.
 */
export type RelayHealthState = "no-relay" | "not-in-use" | "no-closures" | "unknown" | "healthy" | "degraded" | "failing";

export type RelayHealth = {
    state: RelayHealthState;
    /** Tag tone, matching the card's other rows. */
    tone: "safe" | "warn" | "crit" | "mute";
    /** The tag itself — two words at most, because it sits in a column beside six other rows. */
    label: string;
    /** The sub-line under "Relay": why it says that, and when it last closed. */
    detail: string;
};

/**
 * What to show when the lookup itself failed. A failed fetch is "no information", never "no relay" —
 * the difference between the two is a truck someone drives out unarmed.
 */
export const RELAY_HEALTH_UNREADABLE: RelayHealth = {
    state: "unknown",
    tone: "mute",
    label: "Unknown",
    detail: "Could not read relay health — try again shortly",
};

/** EndpointStatus (the server's verdict over persisted history) as the severity deliveryHealth() speaks. */
const severityOf = (status: EndpointStatus | undefined): EndpointSeverity => {
    switch (status) {
        case EndpointStatus.Error:
            return "error";
        case EndpointStatus.Warning:
            return "warn";
        case EndpointStatus.Healthy:
            return "ok";
        default:
            return "unknown";
    }
};

/**
 * How reliably it has been closing, when the server sent a window rollup — "98.2% of 340 sends · 30d".
 *
 * Worth a few characters in the sub-line because a relay that fires nine times out of ten is the
 * failure mode crews actually report, and it looks identical to a perfect one in every other row here.
 */
const rateDetail = (relay: ClientEndpoint): string | undefined => {
    const window = relay.health;
    if (!window) return undefined;
    const rate = formatRate(window);
    if (!rate) return undefined;
    const span = formatWindowSpan(window);
    return span ? `${rate} · ${span}` : rate;
};

const joinDetail = (...parts: (string | undefined)[]): string => parts.filter(Boolean).join(" · ");

/**
 * The relay row's verdict. `now` is injectable so the "last closed" phrasing is testable.
 */
export function relayHealth(relay: ClientEndpoint | undefined, now = new Date()): RelayHealth {
    if (!relay) {
        return {
            state: "no-relay",
            tone: "mute",
            label: "Not configured",
            // Said plainly rather than left blank: a TMA with no relay endpoint has a horn and beacons
            // that no alert can ever close, and nothing else on this page would show it.
            detail: "No relay on this truck — alerts cannot close the horn or beacons",
        };
    }

    // Nothing points at it, so its history — however old — says nothing about today. The dashboard
    // shows "not in use" here for the same reason: stale rows must not read as a live failure.
    if (relay.inUse === false) {
        return {
            state: "not-in-use",
            tone: "mute",
            label: "Not in use",
            detail: "Wired, but no alert closes it — check the truck's alert settings",
        };
    }

    if (!relay.lastSuccessUtc && !relay.failingSinceUtc) {
        // "No closures yet" is an affirmative claim, so only make it where an absence of rows actually
        // means nothing has been sent. A type that records nothing gets the honest grey instead.
        return relay.reportsDeliveryHistory
            ? {
                  state: "no-closures",
                  tone: "mute",
                  label: "No closures yet",
                  detail: "Wired and armed — nothing has fired it since it was set up",
              }
            : { state: "unknown", tone: "mute", label: "Unknown", detail: "This relay does not report closures" };
    }

    const health = deliveryHealth({
        lastSuccessUtc: relay.lastSuccessUtc,
        consecutiveFailures: relay.consecutiveFailures ?? 0,
        // The cloud's verdict rests on persisted history; it has no device-local buffer state to add.
        historySeverity: severityOf(relay.status),
        recentSendsConsidered: relay.recentSendsConsidered,
        recentFailureCount: relay.recentFailureCount,
        recentSlowSendCount: relay.recentSlowSendCount,
        recentMaxMs: relay.recentMaxMs,
        now,
    });

    const lastClosed = relay.lastSuccessUtc ? `last closed ${formatAgo(relay.lastSuccessUtc, now)}` : undefined;
    const rate = rateDetail(relay);

    if (health.severity === "error") {
        const since = relay.failingSinceUtc ? ` since ${formatDateTime(relay.failingSinceUtc)}` : "";
        const attempts = relay.consecutiveFailures ?? 0;
        return {
            state: "failing",
            tone: "crit",
            label: "Failing",
            // The device's own error is the actionable part — "connection refused" sends someone to the
            // wiring, a timeout to the relay itself — so it leads, with the count and last success after.
            detail: joinDetail(
                `${attempts} failed attempt${attempts === 1 ? "" : "s"}${since}`,
                relay.lastFailureMessage,
                lastClosed,
            ),
        };
    }

    if (health.severity === "warn") {
        return {
            state: "degraded",
            tone: "warn",
            // The reason is the point: "degraded" with no stated cause reads as a false alarm. The
            // window rate is left off here — the reason already quantifies it ("2 of last 5 sends"),
            // and a third clause pushes this row to three wrapped lines in the card's column.
            label: "Degraded",
            detail: joinDetail(health.reason ?? "recent failures", lastClosed),
        };
    }

    if (health.severity === "ok") {
        return { state: "healthy", tone: "safe", label: "Healthy", detail: joinDetail(lastClosed, rate) };
    }

    return { state: "unknown", tone: "mute", label: "Unknown", detail: joinDetail("No recent closures to judge", rate) };
}
